import { Locale } from "@src/Enums/Locale"
import { tState } from "@src/Model/Model"
import { Locale as ILocale } from "@zeguro/schema-validator/dist/types/coco/core/combined"
import Cookies from "js-cookie"
import { useSelector } from "react-redux"

const LOCALE_COOKIE_KEY = "locale"

const getLocaleCookie = (): string | null => {
  return Cookies.get(LOCALE_COOKIE_KEY) || null
}

const setLocaleCookie = (locale: string, webDomain: string): void => {
  const isLocalhost = window.location.hostname === "localhost"
  // Get parent domain shared by all locale subdomains (e.g., "staging.myzeguro.com" from "cocopie.staging.myzeguro.com")
  const parentDomain = webDomain.split(".").slice(1).join(".")
  Cookies.set(LOCALE_COOKIE_KEY, locale, {
    domain: isLocalhost ? undefined : `.${parentDomain}`,
    path: "/",
    expires: 365,
    sameSite: "Lax",
  })
}

type UseLocaleReturn = {
  currentLocale: ILocale | null
  redirectToLocale: (newLocale: ILocale) => void
}

export const useLocale = (): UseLocaleReturn => {
  const webDomainPerLocaleMap = useSelector(
    (state: tState) => state.brand.webDomainPerLocaleMap,
  )
  const webDomain = useSelector((state: tState) => state.brand.webDomain)
  const defaultUILocale = useSelector(
    (state: tState) => state.subscriptionId.defaultUILocale,
  )
  const availableLocales = useSelector(
    (state: tState) => state.subscriptionId.availableLocales,
  )
  const authenticated = useSelector(
    (state: tState) => state.portal.authenticated,
  )
  const userProfile = useSelector((state: tState) => state.portal.userProfile)
  const companyProfileState = useSelector(
    (state: tState) => state.portal.companyProfileState,
  )

  const getCurrentLocale = (): ILocale | null => {
    // Don't proceed if availableLocales hasn't been loaded yet
    if (!availableLocales || availableLocales.length === 0) {
      return null
    }

    // For authenticated users, wait until profile is loaded before deciding locale
    if (authenticated && companyProfileState !== "SUCCESS") {
      return null
    }

    // Step 1: For authenticated users, use their saved locale preference
    if (
      authenticated &&
      userProfile?.locale &&
      availableLocales.includes(userProfile.locale)
    ) {
      return userProfile.locale
    }

    // Step 2: Check cookie for previously selected/stored language
    const storedLocale = getLocaleCookie() as ILocale
    if (storedLocale && availableLocales.includes(storedLocale)) {
      return storedLocale
    }

    // Step 3: Get browser language
    const browserLocale = navigator.language || navigator.languages?.[0] || ""
    // Step 4: Check if exact browser locale is available
    if (availableLocales.includes(browserLocale as ILocale)) {
      setLocaleCookie(browserLocale, webDomain)
      return browserLocale as ILocale
    }

    // Step 5: Try to match by language code
    const browserLangCode = browserLocale.split("-")[0].toLowerCase()
    const matchingLocale = availableLocales.find((locale) =>
      locale.toLowerCase().startsWith(browserLangCode),
    )
    if (matchingLocale) {
      setLocaleCookie(matchingLocale, webDomain)
      return matchingLocale
    }

    // Step 6: No match found, use defaultUILocale or fallback
    const fallbackLocale =
      defaultUILocale || availableLocales[0] || Locale.EN_US
    setLocaleCookie(fallbackLocale, webDomain)
    return fallbackLocale
  }

  const redirectToLocale = (newLocale: ILocale) => {
    setLocaleCookie(newLocale, webDomain)

    if (window.location.hostname === "localhost") {
      return
    }

    const targetDomain = webDomainPerLocaleMap[newLocale]
    if (!targetDomain) {
      console.error("No target domain found for locale:", newLocale)
      return
    }

    const currentPath =
      window.location.pathname + window.location.search + window.location.hash
    const targetUrl = `https://${targetDomain}${currentPath}`
    const currentUrl = window.location.href

    if (targetUrl === currentUrl) {
      return
    }

    window.location.href = targetUrl
  }

  return {
    currentLocale: getCurrentLocale(),
    redirectToLocale,
  }
}
